import Phaser from "phaser";

export const COMBAT_COLOR_HIT = "#f5f5dc";
export const COMBAT_COLOR_CRIT = "#ffc107";
export const COMBAT_COLOR_CRIT_STROKE = "#b45309";
export const COMBAT_COLOR_MISS = "#9ca3af";
export const CRIT_TINT_GOLD = 0xffc107;

export type FloaterKind = "hit" | "crit" | "miss";

type PooledText = Phaser.GameObjects.Text & { _poolIdle?: boolean };

export class FloatingCombatTextPool {
  private pool: PooledText[] = [];
  private readonly scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  spawn(worldX: number, worldY: number, kind: FloaterKind, damage?: number): void {
    const spread = Phaser.Math.Between(-8, 8);
    const x = worldX + spread;
    const y = worldY - 28;

    const text = this.acquire();
    if (kind === "miss") {
      text.setText("MISS");
      text.setFontSize(16);
      text.setColor(COMBAT_COLOR_MISS);
      text.setStroke("#4b5563", 2);
    } else {
      const value = Math.max(1, Math.floor(damage ?? 1));
      text.setText(`-${value}${kind === "crit" ? "!" : ""}`);
      text.setFontSize(kind === "crit" ? 24 : 18);
      text.setColor(kind === "crit" ? COMBAT_COLOR_CRIT : COMBAT_COLOR_HIT);
      text.setStroke(kind === "crit" ? COMBAT_COLOR_CRIT_STROKE : "#1f2937", kind === "crit" ? 3 : 2);
    }

    text.setPosition(x, y);
    text.setAlpha(1);
    text.setScale(kind === "crit" ? 0.6 : 0.55);
    text.setDepth(50_000);
    text.setVisible(true);

    const rise = kind === "crit" ? 48 : 36;
    this.scene.tweens.add({
      targets: text,
      y: y - rise,
      alpha: 0,
      scale: kind === "crit" ? 1.15 : 1,
      duration: kind === "crit" ? 780 : 650,
      ease: "Cubic.easeOut",
      onComplete: () => this.release(text),
    });
  }

  private acquire(): PooledText {
    const idle = this.pool.find((t) => t._poolIdle);
    if (idle) {
      idle._poolIdle = false;
      return idle;
    }
    const text = this.scene.add.text(0, 0, "", {
      fontFamily: "system-ui, sans-serif",
      fontStyle: "bold",
    }) as PooledText;
    text.setOrigin(0.5, 1);
    text._poolIdle = false;
    this.pool.push(text);
    return text;
  }

  private release(text: PooledText): void {
    text.setVisible(false);
    text._poolIdle = true;
  }

  destroy(): void {
    for (const t of this.pool) {
      t.destroy();
    }
    this.pool = [];
  }
}
